/**
 * Advanced → HTTP connections — the whole API of the screen that configures
 * an application's named HTTP endpoints: what a wizard `fetch_url` step's
 * `request.connection` names (a weighing scale's gateway, a carrier API).
 *
 * Administration on the PLATFORM host: base URLs and keys are not part of
 * running the application (`/http-connections` is not in the bound-domain
 * allow-list). Header VALUES are write-only — every response masks them; send
 * a value only to change it, `"-"` (or `remove: true`) to remove the header.
 */

import api from './client'

export interface HttpConnectionHeader {
  name: string
  /** `••••3f9a` — never the value itself. */
  value_masked: string
}

export interface HttpConnection {
  id: number
  name: string
  description: string
  base_url: string
  headers: HttpConnectionHeader[]
  /** Derived from base_url: where the connection's requests may go. */
  allow_hosts: string[]
  enabled: boolean
  updated_at: string | null
}

export interface HttpConnectionHeaderInput {
  name: string
  /** Omitted / blank = unchanged; `"-"` removes the header. */
  value?: string
  remove?: boolean
}

export interface HttpConnectionInput {
  name?: string
  description?: string
  base_url?: string
  headers?: HttpConnectionHeaderInput[]
  enabled?: boolean
}

export interface HttpConnectionsOverview {
  ok: boolean
  connections: HttpConnection[]
}

export interface HttpConnectionProbe {
  ok: boolean
  status: number | null
  error?: string
  /** The start of the answer, so a wrong path is visible. */
  preview?: string
}

const base = (project: string) => `/http-connections/${encodeURIComponent(project)}`

export async function getHttpConnections(project: string): Promise<HttpConnectionsOverview> {
  return (await api.get(base(project))).data
}

export async function addHttpConnection(project: string, body: HttpConnectionInput): Promise<HttpConnectionsOverview> {
  return (await api.post(base(project), body)).data
}

export async function updateHttpConnection(project: string, id: number, body: HttpConnectionInput): Promise<HttpConnectionsOverview> {
  return (await api.put(`${base(project)}/${id}`, body)).data
}

export async function deleteHttpConnection(project: string, id: number): Promise<HttpConnectionsOverview> {
  return (await api.delete(`${base(project)}/${id}`)).data
}

export async function testHttpConnection(project: string, id: number, path = ''): Promise<HttpConnectionProbe> {
  return (await api.post(`${base(project)}/${id}/test`, { path })).data
}

/** The connection names a wizard step may pick (enabled ones); [] when the
 *  caller may not read them or the call fails — the picker then falls back to
 *  free text. */
export async function listHttpConnectionNames(project: string): Promise<string[]> {
  try {
    const data = await getHttpConnections(project)
    return (data.connections || []).filter(c => c.enabled).map(c => c.name)
  } catch {
    return []
  }
}
